import { CreateDepositRequestDto } from '@starsoft/common/dto';
import { DepositMethodId } from '@starsoft/common/enums';
import { useAxiosInstance } from '@starsoft/common/hooks';
import { useMutation } from '@tanstack/react-query';
import { AxiosInstance } from 'axios';
import { useDepositCallback } from '../../../../hooks/deposit';
import useDepositSearchParams from '../../../../hooks/deposit/useDepositSearchParams';
import { createDeposit } from './service';

/**
 * Custom hook to handle deposit operations for BetterBro methods.
 *
 * This hook utilizes the `useMutation` from React Query to manage the deposit
 * operation. It determines if an external redirect is needed based on the deposit
 * method ID and executes the deposit function accordingly.
 *
 * @returns {Object} - Returns an object containing the deposit mutation function and loading state.
 * @returns {Function} return.createDepositBetterBro - Function to initiate a deposit operation.
 * @returns {boolean} return.isLoading - Indicates if a deposit operation is currently loading.
 */
export function useCreateDeposit() {
  // Retrieve the deposit method ID from the search parameters
  const { depositMethodId } = useDepositSearchParams();

  // Determine if the deposit method requires an external redirect
  const hasExternalRedirect = [
    DepositMethodId.BetterbroMercadoPago?.toString(),
    DepositMethodId.BetterbroMexicanSpei?.toString(),
    DepositMethodId.BetterbroUpi?.toString(),
    DepositMethodId.PayGamesIndiaUpi?.toString(),
    DepositMethodId.PayGamesIndonesiaQris?.toString(),
    DepositMethodId.PayGamesNigeria?.toString(),
    DepositMethodId.PayGamesSingaporePayNow?.toString(),
    DepositMethodId.PayGamesSouthAfrica?.toString(),
  ].includes(depositMethodId);

  // Get an Axios instance for making HTTP requests
  const axiosInstance: AxiosInstance = useAxiosInstance();

  // Get the success callback function for the deposit operation
  const { onSuccess } = useDepositCallback(hasExternalRedirect);

  // Use the useMutation hook to manage the deposit operation
  const { mutate, isPending } = useMutation({
    // Function to execute the deposit operation
    mutationFn: (data: CreateDepositRequestDto) =>
      createDeposit(data, axiosInstance),
    onSuccess, // Callback function to execute on successful deposit
  });

  // Return the deposit mutation function and loading state
  return {
    createDeposit: mutate, // Function to initiate a deposit operation
    isLoading: isPending, // Indicates if a deposit operation is currently loading
  };
}
